import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const ThemeContext = createContext();
const STORAGE_KEY = "kiwami_theme_preference"; // 'auto' | 'light' | 'dark'

// Returns the resolved theme based on current local time when preference is 'auto'.
// Rule: 7:00 ≤ hour < 19:00 → light · else dark.
function resolveFromTime() {
  const h = new Date().getHours();
  return h >= 7 && h < 19 ? "light" : "dark";
}

export const ThemeProvider = ({ children }) => {
  const [preference, setPreference] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === "light" || v === "dark" || v === "auto") return v;
    } catch {
      /* ignore */
    }
    return "auto";
  });

  const [mode, setMode] = useState(() =>
    preference === "auto" ? resolveFromTime() : preference,
  );

  // Recompute mode when preference changes
  useEffect(() => {
    queueMicrotask(() =>
      setMode(preference === "auto" ? resolveFromTime() : preference),
    );
  }, [preference]);

  // Re-evaluate every minute when in 'auto' mode so 19:00 transitions update live
  useEffect(() => {
    if (preference !== "auto") return;
    const interval = setInterval(() => {
      const next = resolveFromTime();
      queueMicrotask(() => setMode((prev) => (prev !== next ? next : prev)));
    }, 60_000);
    return () => clearInterval(interval);
  }, [preference]);

  // Persist preference + reflect mode on <html data-theme="...">
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      /* ignore */
    }
  }, [preference]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const cyclePreference = useCallback(() => {
    // auto → light → dark → auto
    setPreference((p) => (p === "auto" ? "light" : p === "light" ? "dark" : "auto"));
  }, []);

  return (
    <ThemeContext.Provider
      value={{ mode, preference, setPreference, cyclePreference }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};
